/// This function is uncallable in TypeScript, so the compiler will complain if it is called.
/// It can thus be used to make switches and checks exhaustive, by passing the checked value in here in the default case.
import { R } from '@/shared/EnumResources.resx';
import { Log } from '@/shared/Log';

export function assertExhausted(unreachable: never) {}

export function getEnumTranslationByKey<T extends object>(
    enumObj: T,
    value: T[keyof T],
    suffix: string | null = null,
): string {
    if (value == null) {
        return '';
    }

    const key = (Object.keys(enumObj) as Array<keyof T>).find(
        (key) => enumObj[key] === value,
    ) as string;
    const fullKey =
        `${getEnumName(enumObj)}_${key}` + (suffix != null ? '_' + suffix : '');
    const result = getResourceForEnum(fullKey);
    if (result === fullKey) {
        Log.error('Cannot find enum for key', fullKey);
    }
    return result;
}
export function getIndefiniteEnumTranslation<T extends object>(
    enumObj: T,
    value: T[keyof T],
): string {
    return getEnumTranslationByKey(enumObj, value, 'Indefinite');
}

export const getEnumKeyName = <_, T extends object>({
    enumObject,
    value,
}: {
    enumObject: T;
    value: T[keyof T];
}) => {
    return Object.keys(enumObject).find(
        (key) => enumObject[key as keyof T] === value,
    );
};

export function getEnumKeys<T extends Record<string, string | number>>(
    enumObj: T,
): (keyof T)[] {
    return Object.keys(enumObj).filter((key) =>
        isNaN(Number(key)),
    ) as (keyof T)[];
}

export function getEnumOptions<T extends Record<string, string | number>>(
    enumObj: T,
): Array<{ value: number; label: string }> {
    return getEnumKeys(enumObj).map((key) => ({
        value: enumObj[key] as number,
        label: getEnumTranslationByKey(enumObj, enumObj[key]),
    }));
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
}

export enum ProgramTypeEnum {
    Menopause = 0,
    SundVaegt = 1,
    NnSundVaegt = 2,
    NewbornPfa = 3,
    NewbornStorstrom = 4,
    Acne = 5,
}

export enum ConsultationTypeEnum {
    Message = 0,
    Video = 1,
    Prescription = 2,
    MidwifeMessage = 3,
    MidwifeVideo = 4,
    NurseMessage = 5,
    NurseVideo = 6,
    DentistMessage = 7,
    DentistVideo = 8,
    DermatologistMessage = 9,
    DermatologistVideo = 10,
    RheumatologistVideo = 11,
    PhysiotherapistStartupVideo = 12,
    PsychologistVideo = 13,
    AllergyCheckVideo = 14,
    SundVaegtStartupMessage = 15,
    SundVaegtNurseCheckupVideo = 16,
    SundVaegtDoctorCheckupVideo = 17,
    SundVaegtNurseCheckupMessage = 18,
    SundVaegtNurseAdHocVideo = 19,
    SundVaegtQuestionnaireMessage = 20,
    PhysiotherapistMessage = 21,
    PhysiotherapistCheckupVideo = 22,
    PhysiotherapistQuestionnaireMessage = 23,
    DietitianStartupVideo = 24,
    DietitianCheckupVideo = 25,
    PsychologistCrisisVideo = 26,
    PersonalHealthProgrammeStartup = 27,
    AcneProgrammeStartupMessage = 28,
    AcneProgrammeStartupVideo = 29,
    AcneProgrammeCheckupVideo = 30,
    AcneDermatologistMessage = 31,
    VeterinarianVideo = 32,
    DietitianUploadAttachmentMessage = 33,
    DynamicCodeVideo = 34,
    VeterinarianMessage = 35,
    PracticeManagerMessage = 36,
    NnStartupVideo = 37,
    NnNurseCheckupVideo = 38,
    NnDietitianCheckupVideo = 39,
    NnDoctorMessage = 40,
    NnScreeningQuestionnaireMessage = 41,
    NnCheckupQuestionnaireMessage = 42,
    NnExitQuestionnaireMessage = 43,
    PsychologistMessage = 44,
    PsychologistFamilyTherapyVideo = 45,
    MenopauseStartupVideo = 46,
    MenopauseOffboardingVideo = 47,
    MenopauseNurseCheckupVideo = 48,
    MenopauseNurseMessage = 49,
    DietitianMessage = 50,
    SundVaegtExitQuestionnaireMessage = 51,
    MenopauseDietitianVideo = 52,
    SundVaegtFeedbackQuestionnaireMessage = 53,
    MenopauseHalfwayVideo = 54,
}

export enum ConsultationCategoryFilterEnum {
    Video = 0,
    Text = 1,
}

/**
 * Represents the types of target a caregiver template can be used for.
 * Uses flags to allow multiple types per template.
 */
export enum TemplateTargetTypeFlags {
    None = 0,
    Text = 1,
    Video = 2,
    ClinicalMessage = 4,
}

export enum UserTypeEnum {
    Patient = 0,
    Doctor = 1,
    Admin = 2,
    CompanyManager = 4,
    CompanyDashboardReader = 5,
}

export enum PetTypeEnum {
    Dog = 0,
    Cat = 1,
    Rabbit = 2,
    Rodent = 3,
}

export enum ReferralOptionEnum {
    SpecialistsAndPrivateHospitals = 0,
    Physiotherapy = 1,
    Psychologist = 2,
}

export enum VetConsultationReasonEnum {
    Gastrointestinal = 0,
    SkinCoatEars = 1,
    Poisoning = 2,
    Eyes = 3,
    UrinaryTract = 4,
    Lameness = 5,
    RespiratoryTract = 6,
    OralCavity = 7,
    Pain = 8,
    Claws = 9,
    GenitalsMammaryGlands = 10,
    ReproductionCycle = 11,
    Behaviour = 12,
    Prescription = 13,
    OtherReason = 1000,
}

export enum VetConsultationOutcomeEnum {
    AdvisedForHomeTreatment = 0,
    ReferredDuringRegularOpeningHours = 1,
    ReferredOutsideRegularOpeningHours = 2,
    RequestedMoreInformation = 3,
}

export enum PhysioConsultationOutcomeEnum {
    ConservativeApproachPatientContinuesAlone = 0,
    OfferedFollowUpConsultation = 1,
    ReturnedToVisitationForPhysicalTreatment = 2,
    ReturnedToOwnDoctor = 3,
    NoShow = 4,
    NeedMoreInformation = 5,
    OfferedVideoConsultation = 6,
}

export enum PetSexEnum {
    Female,
    FemaleSterilized,
    Male,
    MaleNeutered,
    Unknown,
}

export enum SexEnum {
    Unknown,
    Female,
    Male,
}

export enum CaregiverSpecialization {
    FamilyTherapy = 0,
    Menopause = 1,
    SundVaegt = 2,
    Pediatric = 3,
    ClinicalPsychologist = 4,
}

export enum CaregiverRoleEnum {
    Doctor = 0,
    Nurse = 1,
    Dentist = 2,
    Midwife = 3,
    Dermatologist = 4,
    Rheumatologist = 5,
    Physiotherapist = 6,
    Psychologist = 7,
    AllergyDoctor = 8,
    SundVaegtDoctor = 9,
    SundVaegtNurse = 10,
    Dietitian = 11,
    AcneDermatologist = 12,
    Veterinarian = 13,
    PracticeManager = 14,
    Gynecologist = 15,
    MenopauseDoctor = 16,
}

export enum PartnerStatusEnum {
    NoPartner = 0,
    Invited = 1,
    Active = 2,
}

export enum SignupDoctorResponseEnum {
    OK,
    Failed,
    EmailExists,
}

export enum OpenVideoConsultationStatusEnum {
    OK = 0,
    NoDoctorsAvailable = 1,
    TimeSlotOverbooked = 2,
    BadRequest = 3,
    PrerequisiteExists = 4,
    TooLateToReschedule = 5,
    AlreadyHasUpcoming = 6,
}

export enum ServiceTypeEnum {
    VideoConsultation = 0,
    Message = 1,
    MessageReply = 2,
    Prescription = 3,
    DermatologistMessage = 4,
    DermatologistMessageReply = 5,
    RheumatologistVideo = 6,
    PhysiotherapistStartupVideo = 7,
    DermatologistVideo = 8,
    PsychologistVideo = 9,
    AllergyCheckVideo = 10,
    SundVaegtStartupMessage = 11,
    SundVaegtNurseCheckupVideo = 12,
    SundVaegtDoctorCheckupVideo = 13,
    SundVaegtNurseCheckupMessage = 14,
    SundVaegtNurseCheckupMessageReply = 15,
    SundVaegtNurseAdHocVideo = 16,
    SundVaegtQuestionnaireMessage = 17,
    PhysiotherapistMessage = 18,
    PhysiotherapistMessageReply = 19,
    PhysiotherapistCheckupVideo = 20,
    SundVaegtQuestionnaireMessageReply = 21,
    PhysiotherapistQuestionnaireMessage = 22,
    SundVaegtStartupMessageReply = 23,
    DietitianStartupVideo = 24,
    DietitianCheckupVideo = 25,
    PsychologistCrisisVideo = 26,
    PersonalHealthProgrammeStartup = 27,
    AcneProgrammeStartupMessage = 28,
    AcneProgrammeStartupVideo = 29,
    AcneProgrammeCheckupVideo = 30,
    AcneDermatologistMessage = 31,
    AcneDermatologistMessageReply = 32,
    VeterinarianVideo = 33,
    DietitianUploadAttachmentMessage = 34,
    DynamicCodeVideo = 35,
    NurseVideo = 36,
    MidwifeVideo = 37,
    NurseMessage = 38,
    NurseMessageReply = 39,
    MidwifeMessage = 40,
    MidwifeMessageReply = 41,
    VeterinarianMessage = 42,
    VeterinarianMessageReply = 43,
    PracticeManagerMessage = 44,
    NnStartupVideo = 45,
    NnNurseCheckupVideo = 46,
    NnDietitianCheckupVideo = 47,
    NnDoctorMessage = 48,
    NnDoctorMessageReply = 49,
    NnScreeningQuestionnaireMessage = 50,
    NnCheckupQuestionnaireMessage = 51,
    NnExitQuestionnaireMessage = 52,
    PsychologistMessage = 53,
    PsychologistFamilyTherapyVideo = 54,
    MenopauseStartupVideo = 55,
    MenopauseOffboardingVideo = 56,
    MenopauseNurseCheckupVideo = 57,
    MenopauseNurseMessage = 58,
    MenopauseNurseMessageReply = 59,
    DietitianMessage = 60,
    DietitianMessageReply = 61,
    SundVaegtExitQuestionnaireMessage = 62,
    MenopauseDietitianVideo = 63,
    SundVaegtFeedbackQuestionnaireMessage = 64,
    MenopauseHalfwayVideo = 65,
}

export enum OrderStatusEnum {
    ProcessingInEpay = 1,
    ProcessingInLaegecheck = 2,
    Completed = 3,
    Canceled = 4,
}

export enum AvailableRelationTypeEnum {
    Child = 0,
    AdultHouseholdMember = 1,
    Owner = 2,
}

export enum AuditProblemCategory {
    IncorrectDiagnosis = 0,
    InsufficientTreatment = 1,
    MedicationError = 2,
    DocumentationError = 3,
    CommunicationIssue = 4,
    OtherIssue = 5,
}

export enum DeviceTypeEnum {
    Browser = 0,
    iOS = 1,
    Android = 2,
}

export enum PaymentProvider {
    Company = 0,
    AppStore = 1,
    GoogleWallet = 2,
    ePay = 3,
    DIBS = 4,
    Subscription = 5,
    Coupon = 6,
    ReepayCheckout = 7,
    GracePeriod = 8,
    PriceOverride = 9,
    Free = 10,
    CreatedByCaregiver = 11,
    HouseholdAccess = 12,
}

export enum InsurancePolicyProviderType {
    None = 0,
    Falck = 1,
    Dss = 2,
    Lego = 3,
    If = 4,
    FalckHealthcare = 5,
    ApPension = 6,
    Pfa = 7,
    Codan = 8,
    CodanIndboforsikring = 9,
    Privatsikring = 10,
    Popermo = 11,
    AlmBrand = 12,
    Langelandsfestival = 13,
    Bdo = 14,
    Kf = 15,
    Jyske = 16,
    Vestjylland = 17,
    Forsia = 18,
    Lb = 19,
    Bauta = 20,
    Runa = 21,
    LbPfa = 22,
    DetFagligeHusForsikring = 23,
    EuroAccident = 24,
    DjoefForsikring = 25,
    PPlus = 26,
}

export enum SubscriptionStatusEnum {
    Trialing = 0,
    Active = 1,
    PastDue = 2,
    Canceled = 3,
    Expired = 4,
    OnHold = 5,
    Unknown = 2147483647,
}

export enum PlanInterval {
    Day,
    Week,
    Month,
    Year,
}

export enum PhoneCallRejectedReason {
    ConsultationIsAlreadyTaken = 'ConsultationIsAlreadyTaken',
}

export enum HouseholdMemberType {
    Parent = 0,
    Child = 1,
    Sibling = 2,
    Partner = 3,
    Adolescent = 4,

    PetOwner = 100,
    ManuallyAdded = 101,
}

export enum HouseholdMemberSearchSource {
    ExistingPatient = 0,
    CprRegistryOnly = 1,
}

export enum HouseholdJournalNoteMemberStatus {
    Created = 0,
    PatientNotFound = 1,
    Failed = 2,
}

export enum EpicrisisDocumentType {
    DischargeLetter = 0,
    OutPatientDischargeLetter = 1,
    CasualtyWardLetter = 2,
    RadiologyReport = 3,
    PrivateSpecialistLetter = 4,
}

export enum ReferralType {
    Physiotherapy = 0,
    Hospital = 1,
    Psychologist = 2,
    Specialist = 3,
}

export enum HospitalPriorityOptionsEnum {
    Urgent = 0,
    Elective = 1,
}
export enum HospitalTransportOptionsEnum {
    None = 0,
    Seated = 1,
    Lying = 2,
}
export enum HospitalStatusOptionsEnum {
    Outpatient = 0,
    Admission = 1,
}

export enum PsychologyDiagnosis {
    RobberyViolenceOrRapeVictim = 1,
    TrafficOrAccidentVictim = 2,
    RelativesOfSeriouslyMentallyIllPersons = 3,
    PersonsAffectedBySeriousDisablingDisease = 4,
    RelativesOfPersonsAffectedBySeriousDisablingDisease = 5,
    RelativesInCaseOfDeath = 6,
    PersonsWhoHaveAttemptedSuicide = 7,
    WomenUndergoingAbortionAfter12WeeksOfPregnancy = 8,
    PersonsUnder18WhoHaveBeenVictimsOfIncestOrOtherSexualAbuse = 9,
    PersonsWithMildToModerateDepressionAfterPsychometricTest = 10,
    PersonsWithMildToModerateAnxietyIncludingMildToModerateOCD = 11,
    DeviationFrom6Months = 99,
}

export enum SpecialistVisitationOptionsEnum {
    InternMedicine = 0,
}

export enum SpecialistReferralCode {
    TreatmentAndInvestigation = 1,
    Radiology = 3,
    ContinuedTreatment = 4,
    Visit = 5,
    SingleConsultationAssessment = 6,
}

export enum LanguageCode {
    da = 0,
    en = 1,
    nb = 2,
}

// Enums and their translations have to be kept in Models/EnumResources.resx as the file present here is a copy for reference to generate files for translations.
const enumNameMap = new Map<object, string>([
    [PaymentProvider, 'ConsultationPaymentProviderEnum'],
    [PetSexEnum, 'PetSex'],
    [PetTypeEnum, 'PetType'],
    [ReferralOptionEnum, 'ReferralOptionEnum'],
    [SubscriptionStatusEnum, 'SubscriptionStatusEnum'],
    [UserTypeEnum, 'UserTypeEnum'],
    [ServiceTypeEnum, 'ServiceTypeEnum'],
    [PlanInterval, 'PlanInterval'],
    [ConsultationTypeEnum, 'ConsultationTypeEnum'],
    [CaregiverRoleEnum, 'CaregiverRoleEnum'],
    [CaregiverSpecialization, 'CaregiverSpecialization'],
    [VetConsultationOutcomeEnum, 'VetConsultationOutcomeEnum'],
    [VetConsultationReasonEnum, 'VetConsultationReasonEnum'],
    [PhysioConsultationOutcomeEnum, 'PhysioConsultationOutcomeEnum'],
    [ConsultationCategoryFilterEnum, 'ConsultationCategoryFilterEnum'],
    [ReferralType, 'ReferralType'],
    [SpecialistVisitationOptionsEnum, 'SpecialistVisitationOptionsEnum'],
    [SpecialistReferralCode, 'SpecialistReferralCode'],
    [PsychologyDiagnosis, 'PsychologyDiagnosis'],
    [HospitalPriorityOptionsEnum, 'HospitalPriorityOptionsEnum'],
    [HospitalTransportOptionsEnum, 'HospitalTransportOptionsEnum'],
    [HospitalStatusOptionsEnum, 'HospitalStatusOptionsEnum'],
    [EpicrisisDocumentType, 'EpicrisisDocumentType'],
    [AuditProblemCategory, 'AuditProblemCategory'],
]);
function getResourceForEnum(value: string) {
    return R[value as keyof typeof R] ?? value;
}

export function getResourceForEnumIfExists(value: string) {
    return R[value as keyof typeof R] ?? null;
}

function getEnumName<T>(enumObj: T): string | undefined {
    return enumNameMap.get(enumObj as object);
}
